import React from 'react';
import { useParams } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import {
  Table, TableHead, TableRow, TableCell, TableBody, Grid,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import useCompetitionLeaderboard from 'hooks/useCompetitionLeaderboard';
import Paper from 'components/system/Paper';
import { ContentLoading } from 'components/system/Loading';
import useCompetitionStore from 'store/competitionStore';
import { avatarStyles } from 'utils/nameColorPicker';
import millisToHours from 'utils/millisToHours';
import colors from 'styles/colors';
import { mediumWithoutSecondsFormatter } from 'utils/formatDateTime';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useLocalStorage from 'hooks/useLocalStorage';
import { toNumberOrDefault } from 'utils/helpers';
import EmptyLeaderBoard from './EmptyLeaderboard';

const TotalPoints = (
  <Typography
    component="span"
    gutterBottom
    sx={{
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: '700',
      marginBottom: '0',
      display: 'flex',
    }}
  >
    Score
    <Tooltip
      arrow
      sx={{ backgroundColor: colors.background.primary }}
      title="TOTAL points for the solved problems"
      placement="top"
    >
      <InfoOutlinedIcon sx={{
        fontSize: '14px',
        paddingLeft: '5px',
      }}
      />
    </Tooltip>
  </Typography>
);

const TotalTimePenalty = (
  <Typography
    component="span"
    gutterBottom
    sx={{
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: '700',
      marginBottom: '0',
      display: 'flex',
    }}
  >
    Time penalty
    <Tooltip
      arrow
      sx={{ backgroundColor: colors.background.primary }}
      title="TOTAL time it took to solve the problems. Wrong submissions do not count."
      placement="top"
    >
      <InfoOutlinedIcon sx={{
        fontSize: '14px',
        paddingLeft: '5px',
      }}
      />
    </Tooltip>
  </Typography>
);

const leaderboardColumns = [
  {
    key: 'rank',
    label: 'Rank',
  },
  {
    key: 'displayName',
    label: 'Name',
  },
  {
    key: 'totalPoints',
    label: TotalPoints,
  },
  {
    key: 'totalTimePenalty',
    label: TotalTimePenalty,
  },
  {
    key: 'totalSubmissions',
    label: 'Submissions',
  },
  {
    key: 'lastSubmitted',
    label: 'Last Submitted',
  },
];

const Leaderboard = () => {
  const { id: competitionId } = useParams();
  const awardCount = useCompetitionStore((state) => state.competition.details.awardCount);

  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    perPage: StringParam,
  });
  const [storedValue, setValue] = useLocalStorage('leaderboard_table_row', 50);

  const {
    page = 1,
    perPage = storedValue,
  } = query;

  const {
    leaderBoardUsers,
    total,
    isLoading,
  } = useCompetitionLeaderboard(competitionId, page - 1, perPage);

  const handleChangePage = (clickedPage, newPage) => {
    setQuery({ ...query, page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (event) => {
    const rowPerPage = parseInt(event.target.value, 10);
    setValue(rowPerPage);
    setQuery({ perPage: rowPerPage, page: 1 });
  };

  if (isLoading) return (<ContentLoading />);

  return (
    <Grid item xs={12}>
      {leaderBoardUsers.length ? (
        <Paper sx={{ overflowX: 'auto', borderRadius: '8px' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: '1px',
                    margin: 0,
                    padding: 0,
                  }}
                  align="left"
                />
                {
                  leaderboardColumns.map(({ key, label }) => (
                    <TableCell key={key} align="left">{label}</TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {leaderBoardUsers?.map((leaderBoardItem, index) => {
                const isAwarded = index < awardCount && page === 1;

                return (
                  <TableRow
                    key={leaderBoardItem.userId}
                    sx={{
                      '&:last-child th': { border: 0 },
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: `${colors.background.secondary}`,
                      },
                      '&:active': {
                        backgroundColor: `${colors.background.secondary}`,
                      },
                      '&:focused': {
                        backgroundColor: `${colors.background.secondary}`,
                      },
                    }}
                  >
                    <TableCell sx={{
                      width: '1px', padding: 0, margin: 0, position: 'relative',
                    }}
                    >
                      {isAwarded && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '-6px',
                            top: '3px',
                            bottom: '3px',
                            width: '6px',
                            borderTopLeftRadius: '4px',
                            borderBottomLeftRadius: '4px',
                            backgroundColor: colors.primary.main,
                          }}
                        />
                      )}
                    </TableCell>
                    {
                      leaderboardColumns.map(({ key }) => {
                        if (key === 'displayName') {
                          return (
                            <TableCell key={key} align="left">
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                  alt={`${leaderBoardItem.displayName || 'avatar'}`}
                                  src={leaderBoardItem.avatar}
                                  sx={avatarStyles(leaderBoardItem.displayName, '', {
                                    height: '40px',
                                    width: '40px',
                                    fontSize: '18px',
                                    textTransform: 'capitalize',
                                  })}
                                >
                                  {leaderBoardItem.displayName[0]}
                                </Avatar>
                                {leaderBoardItem.displayName || ' - '}
                              </Box>
                            </TableCell>
                          );
                        }
                        if (key === 'totalTimePenalty') {
                          return (
                            <TableCell key={key} align="left">
                              {leaderBoardItem[key] ? (
                                <Typography
                                  component="span"
                                  className="monospace"
                                  gutterBottom
                                  sx={{ color: colors.error.light, fontSize: '14px' }}
                                >
                                  {millisToHours(leaderBoardItem[key] * 1000)}
                                </Typography>
                              ) : ' - '}
                            </TableCell>
                          );
                        }
                        if (key === 'totalPoints') {
                          return (
                            <TableCell key={key} align="left">
                              {leaderBoardItem[key] ? (
                                <Typography
                                  component="span"
                                  className="monospace"
                                  gutterBottom
                                  sx={{ color: colors.success.light, fontSize: '14px' }}
                                >
                                  {leaderBoardItem[key]}
                                </Typography>
                              ) : ' - '}
                            </TableCell>
                          );
                        }
                        if (key === 'lastSubmitted') {
                          return (
                            <TableCell key={key} align="left">
                              {leaderBoardItem[key] ? mediumWithoutSecondsFormatter(leaderBoardItem[key]) : ' - '}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={key} align="left">
                            {leaderBoardItem[key] || ' - '}
                          </TableCell>
                        );
                      })
                    }
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            sx={{ overflow: 'visible' }}
            component="div"
            count={total}
            page={page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={toNumberOrDefault(perPage, 50)}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : <EmptyLeaderBoard />}
    </Grid>
  );
};
Leaderboard.displayName = 'Leaderboard';

export default Leaderboard;
